/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedButton from 'bundles/page/components/TrackedButton';
import type { FormattedDegree } from 'bundles/unified-description-page-common/components/modules/credits/DegreeCard';
import DegreesModal from 'bundles/unified-description-page-common/components/modules/credits/DegreesModal';
import { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

import _t from 'i18n!nls/xdp';

type Props = {
  degrees: FormattedDegree[];
  modalDescription?: React.ReactNode;
  descriptionDisclaimer?: string;
  modalTitle?: string;
  ctaLabel?: string;
  disableCta?: boolean;
  openInNewTab?: boolean;
  inlineCta?: boolean;
};

const styles = {
  learnMore: css`
    max-width: unset;
    padding: 0;
    margin-left: 0;
    margin-top: var(--cds-spacing-150);
    height: unset;
    width: unset;

    :hover {
      background: none;
      box-shadow: none;
      border: none;
    }
  `,
  inlineCta: css`
    display: inline-flex;

    button {
      margin-top: 0;
    }
  `,
};

const DegreesModalCta = ({
  degrees,
  modalDescription,
  descriptionDisclaimer,
  modalTitle,
  ctaLabel,
  disableCta,
  openInNewTab,
  inlineCta,
}: Props) => {
  const { isModalOpen, setModalOpen } = useModal();
  const eventingProductData = useEventingProductData();
  const track = useTracker();

  if (!degrees || !degrees?.length) {
    return null;
  }

  const onClick = () => {
    track('click_button', {
      button: {
        name: 'description_page_degree_stackability',
      },
      pageSection: {
        sectionName: 'eligible_degree',
      },
      ...eventingProductData,
    });
    setModalOpen();
  };

  return (
    <div css={inlineCta ? styles.inlineCta : ''}>
      <Button
        variant="ghost"
        edgeAlign="start"
        size="small"
        css={styles.learnMore}
        component={TrackedButton}
        requireFullyVisible={false}
        withVisibilityTracking={false}
        trackingName="degrees_modal_cta"
        onClick={onClick}
        data-e2e="degrees-modal-cta"
      >
        {ctaLabel ?? _t('View eligible degrees')}
      </Button>
      <DegreesModal
        title={modalTitle}
        open={isModalOpen}
        setOpen={setModalOpen}
        degrees={degrees}
        description={modalDescription}
        disclaimer={descriptionDisclaimer}
        disableCta={disableCta}
        openInNewTab={openInNewTab}
        trackingData={{ sourceId: eventingProductData.product?.id, carouselId: 'degree_product_card_modal' }}
      />
    </div>
  );
};

export default DegreesModalCta;
